import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Reveal } from "@/components/Reveal";
import { resolveImg } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, CaretLeft, CaretRight } from "@phosphor-icons/react";

const EASE = [0.22, 1, 0.36, 1];

// Stock placeholders were never the client's photography — treat them as "not uploaded yet".
const isStock = (u) => /unsplash\.com|pexels\.com/.test(u || "");
const slotVal = (u) => (u && !isStock(u) ? u : null);

const DEFAULT_ROOM_NAMES = ["Reception area", "Conference room", "Executive cabin 1", "Executive cabin 2", "Pantry"];
const defaultRooms = () => DEFAULT_ROOM_NAMES.map((name) => ({ name, before: [], after: [] }));

// Fallback project list (names only) until real photography is uploaded via the CMS.
const PROCESS_PROJECTS = [
  { name: "Almas Tower JLT", location: "Dubai, UAE", rooms: defaultRooms() },
  { name: "HDS Tower JLT", location: "JLT, Dubai, UAE", rooms: defaultRooms() },
  { name: "The Lakes, Hattan", location: "Dubai, UAE", rooms: defaultRooms() },
];

export default function Journal({ data }) {
  // Prefer CMS-managed projects; fall back to the built-in placeholder set.
  // Slots are positional (1-3) so a before shot always pairs with its after shot.
  const toRoom = (r) => ({
    name: r?.name || "",
    before: [r?.before1, r?.before2, r?.before3].map(slotVal),
    after: [r?.after1, r?.after2, r?.after3].map(slotVal),
  });
  const cmsProjects = (data?.projects || []).map((p) => {
    // new structure: rooms[]; legacy structure: before/after fields on the project itself
    let rooms = (p?.rooms?.length ? p.rooms : [p]).map(toRoom);
    const empty = rooms.every((r) => !r.name && !r.before.some(Boolean) && !r.after.some(Boolean));
    if (empty) rooms = defaultRooms();
    return { name: p?.name || "Untitled Project", location: p?.location || "", rooms };
  });
  const projects = cmsProjects.length ? cmsProjects : PROCESS_PROJECTS;

  return (
    <section id="journal" data-testid="journal-section" className="bg-background py-24 md:py-40">
      <div className="mx-auto max-w-[1500px] px-6 md:px-12">
        {/* centered header */}
        <div className="max-w-3xl mx-auto text-center mb-16 md:mb-24">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.3em] text-foreground/50">{data?.label || "The Process"}</p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-serif font-light text-5xl sm:text-6xl md:text-7xl leading-[1.02] tracking-tight">
              {data?.title || "From raw space"}
              <br />
              <span className="italic text-accent/90">{data?.emphasis || "to finished home"}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 text-sm md:text-base leading-relaxed text-foreground/55 max-w-md mx-auto">
              {data?.body || "Three angle shots capture each space as we found it. Reveal the transformation to see it styled and fully furnished."}
            </p>
          </Reveal>
        </div>

        {/* upcoming — its own section header, styled like "Selected Work" */}
        <div className="mt-28 md:mt-40 mb-10 md:mb-14" data-testid="upcoming-header">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-accent">Before &amp; After</p>
          </Reveal>
        </div>

        {/* process projects */}
        <div className="space-y-24 md:space-y-32">
          {projects.map((p, i) => (
            <Reveal key={i} delay={0.05}>
              <ProcessProject index={i + 1} project={p} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function PhotoLightbox({ project, room, start, startBefore, onClose }) {
  const shots = [0, 1, 2].filter((i) => room.after[i] || room.before[i]);
  const [pos, setPos] = useState(Math.max(0, shots.indexOf(start)));
  const [split, setSplit] = useState(startBefore ? 100 : 0); // % of BEFORE wiped in from the left
  const frameRef = useRef(null);
  const dragging = useRef(false);
  const i = shots[pos] ?? 0;
  const afterSrc = room.after[i];
  const beforeSrc = room.before[i];
  const canCompare = Boolean(afterSrc && beforeSrc);
  const state = canCompare ? (split > 50 ? "Before" : "After") : afterSrc ? "After" : "Before";
  const caption = `${project.name}${room.name ? ` — ${room.name}` : ""}`;

  const step = (dir) => {
    setPos((p) => (p + dir + shots.length) % shots.length);
    setSplit(0); // always land on the styled After first
  };

  const setFromPointer = (clientX) => {
    const rect = frameRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const pct = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    setSplit(pct);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && shots.length > 1) step(-1);
      if (e.key === "ArrowRight" && shots.length > 1) step(1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, shots.length]);

  return createPortal(
    <AnimatePresence>
      <motion.div
        data-testid="photo-lightbox"
        className="fixed inset-0 z-[120] flex items-center justify-center p-4 md:p-6 cursor-zoom-out"
        style={{ background: "rgba(24,20,17,0.92)" }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.35, ease: EASE }}
        onClick={onClose}
      >
        <button
          data-testid="photo-lightbox-close"
          onClick={onClose}
          aria-label="Close photo"
          className="absolute top-5 right-5 md:top-8 md:right-8 z-10 h-11 w-11 rounded-full border border-white/30 text-white/80 grid place-items-center hover:bg-white hover:text-black transition-colors duration-300"
        >
          <X size={20} weight="light" />
        </button>

        {shots.length > 1 && (
          <>
            <button
              data-testid="photo-lightbox-prev"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              aria-label="Previous photo"
              className="absolute left-3 md:left-8 top-1/2 -translate-y-1/2 z-10 h-11 w-11 rounded-full border border-white/30 text-white/80 grid place-items-center hover:bg-white hover:text-black transition-colors duration-300"
            >
              <CaretLeft size={18} weight="light" />
            </button>
            <button
              data-testid="photo-lightbox-next"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              aria-label="Next photo"
              className="absolute right-3 md:right-8 top-1/2 -translate-y-1/2 z-10 h-11 w-11 rounded-full border border-white/30 text-white/80 grid place-items-center hover:bg-white hover:text-black transition-colors duration-300"
            >
              <CaretRight size={18} weight="light" />
            </button>
          </>
        )}

        <motion.div
          className="relative w-[min(1600px,94vw,124vh)] max-w-full cursor-default"
          initial={{ opacity: 0, scale: 0.86, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.92 }}
          transition={{ duration: 0.5, ease: EASE }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            ref={frameRef}
            data-testid="photo-compare-frame"
            className={`relative overflow-hidden bg-black/40 w-full aspect-[3/2] select-none ${canCompare ? "touch-none cursor-ew-resize" : ""}`}
            onPointerDown={
              canCompare
                ? (e) => {
                    dragging.current = true;
                    e.currentTarget.setPointerCapture(e.pointerId);
                    setFromPointer(e.clientX);
                  }
                : undefined
            }
            onPointerMove={canCompare ? (e) => dragging.current && setFromPointer(e.clientX) : undefined}
            onPointerUp={canCompare ? () => (dragging.current = false) : undefined}
            onPointerCancel={canCompare ? () => (dragging.current = false) : undefined}
          >
            <motion.img
              key={`${i}-base`}
              src={resolveImg(afterSrc || beforeSrc)}
              alt={`${caption}, ${afterSrc ? "after" : "before"}`}
              className="absolute inset-0 h-full w-full object-cover shadow-2xl"
              draggable={false}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.45, ease: EASE }}
            />
            {canCompare && (
              <>
                <img
                  src={resolveImg(beforeSrc)}
                  alt={`${caption}, before`}
                  draggable={false}
                  className="absolute inset-0 h-full w-full object-cover"
                  style={{ clipPath: `inset(0 ${100 - split}% 0 0)` }}
                />
                <div
                  data-testid="photo-compare-handle"
                  className="absolute top-0 bottom-0 z-10 -translate-x-1/2"
                  style={{ left: `${split}%` }}
                >
                  <span className="absolute top-0 bottom-0 left-1/2 -translate-x-1/2 w-px bg-white/85 shadow-[0_0_10px_rgba(0,0,0,0.5)]" />
                  <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center h-10 w-10 rounded-full bg-white text-black shadow-lg">
                    <CaretLeft size={12} weight="bold" />
                    <CaretRight size={12} weight="bold" />
                  </span>
                </div>
                {split > 12 && (
                  <span className="absolute top-3 left-3 text-[9px] uppercase tracking-[0.28em] text-white/90 px-2 py-1 bg-black/35 backdrop-blur-sm pointer-events-none">
                    Before
                  </span>
                )}
                {split < 88 && (
                  <span className="absolute top-3 right-3 text-[9px] uppercase tracking-[0.28em] text-white/90 px-2 py-1 bg-black/35 backdrop-blur-sm pointer-events-none">
                    After
                  </span>
                )}
              </>
            )}
          </div>

          <div className="mt-3 grid grid-cols-3 items-center gap-4">
            <p className="text-[10px] uppercase tracking-[0.28em] text-white/60">
              {caption} · {state}
            </p>
            {canCompare ? (
              <span
                data-testid="photo-compare-hint"
                className="justify-self-center text-center text-xs md:text-sm uppercase tracking-[0.3em] text-white/85"
              >
                Drag to compare
              </span>
            ) : (
              <span />
            )}
            {shots.length > 1 ? (
              <span data-testid="photo-lightbox-counter" className="justify-self-end text-[10px] tracking-[0.25em] text-white/55">
                {pos + 1} / {shots.length}
              </span>
            ) : (
              <span />
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

function ProcessProject({ index, project }) {
  const rooms = project.rooms?.length ? project.rooms : [{ name: "", before: [], after: [] }];
  const [roomIdx, setRoomIdx] = useState(0);
  const [showBefore, setShowBefore] = useState(false);
  const [zoom, setZoom] = useState(null); // { src, caption }
  const room = rooms[Math.min(roomIdx, rooms.length - 1)];
  const hasImages = room.after.some(Boolean) || room.before.some(Boolean);
  const showTabs = rooms.length > 1 || (rooms.length === 1 && rooms[0].name);

  const switchRoom = (i) => {
    setRoomIdx(i);
    setShowBefore(false);
  };

  return (
    <div data-testid="process-project">
      {/* three shots — the styled AFTER shows first; "Reveal the before" crossfades to the raw space */}
      {(() => {
        const slots = hasImages ? [0, 1, 2].filter((i) => room.after[i] || room.before[i]) : [0, 1, 2];
        const cols = slots.length === 1 ? "sm:grid-cols-1" : slots.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-3";
        return (
      <motion.div
        key={roomIdx}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.7, ease: EASE }}
        className={`grid grid-cols-1 ${cols} gap-3 md:gap-4`}
      >
        {slots.map((i) => {
          const afterSrc = room.after[i];
          const beforeSrc = room.before[i];
          const baseSrc = afterSrc || beforeSrc;
          if (!baseSrc) {
            return (
              <div
                key={i}
                data-testid="process-placeholder"
                className="relative overflow-hidden aspect-[4/3] border border-foreground/10"
                style={{ background: "linear-gradient(155deg, hsl(40 22% 94%) 0%, hsl(36 20% 89%) 55%, hsl(33 18% 85%) 100%)" }}
              >
                <div
                  className="absolute inset-0"
                  style={{ background: "radial-gradient(120% 90% at 30% 10%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 55%)" }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-serif italic font-light text-2xl md:text-[1.7rem] tracking-wide text-foreground/40 select-none">
                    upcoming...
                  </span>
                </div>
                <span className="absolute bottom-0 left-6 right-6 h-px bg-accent/25" />
              </div>
            );
          }
          return (
            <div
              key={i}
              data-testid={`process-photo-${index}-${i}`}
              role="button"
              tabIndex={0}
              aria-label="View photo full screen"
              onClick={() => setZoom({ start: i })}
              onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && e.currentTarget.click()}
              className="relative overflow-hidden aspect-[4/3] bg-foreground/5 cursor-zoom-in group/tile"
            >
              <img
                src={resolveImg(baseSrc)}
                alt={`${project.name}${room.name ? ` — ${room.name}` : ""}, shot ${i + 1}, after`}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover/tile:scale-[1.03]"
              />
              {afterSrc && beforeSrc && (
                <motion.img
                  src={resolveImg(beforeSrc)}
                  alt={`${project.name}${room.name ? ` — ${room.name}` : ""}, shot ${i + 1}, before`}
                  className="absolute inset-0 w-full h-full object-cover"
                  initial={false}
                  animate={{ opacity: showBefore ? 1 : 0 }}
                  transition={{ duration: 1.1, ease: EASE, delay: showBefore ? i * 0.14 : 0 }}
                />
              )}
              <span className="absolute top-3 left-3 text-[9px] uppercase tracking-[0.28em] text-white/85 px-2 py-1 bg-black/25 backdrop-blur-sm">
                {showBefore && beforeSrc ? "Before" : afterSrc ? "After" : "Before"}
              </span>
            </div>
          );
        })}
      </motion.div>
        );
      })()}

      {/* caption row */}
      <div className="mt-6 md:mt-7 flex items-end justify-between gap-6">
        <div>
          <div className="flex items-baseline gap-4">
            <span className="font-serif text-lg text-foreground/40">{String(index).padStart(2, "0")}</span>
            <h3 className="font-serif font-light text-2xl md:text-[2rem] leading-tight tracking-tight">{project.name}</h3>
          </div>

          {/* room navigation */}
          {showTabs && (
            <div data-testid="room-tabs" className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2">
              {rooms.map((r, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`room-tab-${index}-${i}`}
                  onClick={() => switchRoom(i)}
                  aria-pressed={i === roomIdx}
                  className={`relative pb-1 text-[10px] uppercase tracking-[0.25em] transition-colors duration-400 ${
                    i === roomIdx ? "text-accent" : "text-foreground/40 hover:text-foreground/70"
                  }`}
                >
                  {r.name || `Room ${i + 1}`}
                  <span
                    className={`absolute left-0 bottom-0 h-px bg-accent transition-all duration-500 ${i === roomIdx ? "w-full" : "w-0"}`}
                  />
                </button>
              ))}
            </div>
          )}

          <p className="mt-3 text-[10px] uppercase tracking-[0.28em] text-foreground/45">
            {!hasImages
              ? `${project.location ? `${project.location} — ` : ""}photography coming soon`
              : showBefore
              ? "The raw space — as we found it"
              : `${project.location ? `${project.location} — ` : ""}styled & furnished`}
          </p>
        </div>

        <button
          type="button"
          data-testid={`process-reveal-${index}`}
          onClick={() => setShowBefore((v) => !v)}
          aria-pressed={showBefore}
          className="group shrink-0 inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-foreground hover:text-accent transition-colors duration-500"
        >
          <span className="hidden sm:inline">{showBefore ? "Show the after" : "Reveal the before"}</span>
          <motion.span
            animate={{ rotate: showBefore ? 45 : 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="flex items-center justify-center w-10 h-10 rounded-full border border-foreground/25 group-hover:border-accent transition-colors duration-500"
          >
            <Plus size={16} weight="light" />
          </motion.span>
        </button>
      </div>

      {zoom && (
        <PhotoLightbox
          project={project}
          room={room}
          start={zoom.start}
          startBefore={showBefore}
          onClose={() => setZoom(null)}
        />
      )}
    </div>
  );
}

